import axios from 'axios'
import { assert } from '../utils/assert'

class QuestionService {
  /**
   * Récupère la liste des questions avec pagination et filtres
   * @param {Object} params - Paramètres de la requête
   * @param {number} params.page - Numéro de page (défaut: 1)
   * @param {number} params.limit - Nombre d'éléments par page (défaut: 10)
   * @param {string} params.search - Recherche textuelle
   * @param {string} params.theme - Filtre par thème
   * @param {string} params.type - Filtre par type
   * @param {string} params.level - Filtre par niveau
   * @param {string} params.audio - Filtre par présence d'audio
   * @param {Array<number>} params.versions - Liste des numéros de versions à filtrer
   * @param {string} params.language - Langue des questions
   * @returns {Promise<Object>} Liste des questions avec pagination
   */
  async getQuestions({
    page = 1,
    limit = 10,
    search = null,
    theme = null,
    type = null,
    level = null,
    audio = null,
    versions = null,
    language = null,
  } = {}) {
    const queryParams = {
      page,
      limit,
    }

    // Ajouter les filtres optionnels
    if (search !== null) {
      queryParams.search = search
    }
    if (theme !== null) {
      queryParams.theme = theme
    }
    if (type !== null) {
      queryParams.type = type
    }
    if (level !== null) {
      queryParams.level = level
    }
    if (audio !== null) {
      queryParams.audio = audio
    }
    if (versions !== null && versions.length > 0) {
      queryParams.versions = versions
    }
    if (language !== null) {
      queryParams.language = language
    }

    try {
      const response = await axios.get('/api/questions', {
        params: queryParams,
        withCredentials: true,
      })
      return response.data
    } catch (error) {
      console.error('Erreur lors de la récupération des questions:', error)
      throw error
    }
  }

  /**
   * Récupère une question par son ID
   * @param {string} id - ID de la question
   * @returns {Promise<Object>} Question
   */
  async getQuestion(id) {
    assert(id, 'id is required')

    try {
      const response = await axios.get(`/api/questions/${id}`, {
        withCredentials: true,
      })
      return response.data
    } catch (error) {
      console.error('Erreur lors de la récupération de la question:', error)
      throw error
    }
  }

  /**
   * Crée une nouvelle question
   * @param {Object} question - Données de la question
   * @returns {Promise<Object>} Question créée
   */
  async createQuestion(question) {
    assert(question, 'question is required')

    try {
      const response = await axios.post('/api/questions', question, {
        withCredentials: true,
      })
      return response.data
    } catch (error) {
      console.error('Erreur lors de la création de la question:', error)
      throw error
    }
  }

  /**
   * Met à jour une question
   * @param {string} id - ID de la question
   * @param {Object} question - Données de la question à mettre à jour
   * @returns {Promise<Object>} Question mise à jour
   */
  async updateQuestion(id, question) {
    assert(id, 'id is required')
    assert(question, 'question is required')

    try {
      const response = await axios.put(`/api/questions/${id}`, question, {
        withCredentials: true,
      })
      return response.data
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la question:', error)
      throw error
    }
  }

  /**
   * Supprime une question
   * @param {string} id - ID de la question
   * @returns {Promise<void>}
   */
  async deleteQuestion(id) {
    assert(id, 'id is required')

    try {
      await axios.delete(`/api/questions/${id}`, {
        withCredentials: true,
      })
    } catch (error) {
      console.error('Erreur lors de la suppression de la question:', error)
      throw error
    }
  }

  /**
   * Annule une opération temporaire sur une question
   * @param {string} id - ID de la question
   * @returns {Promise<void>}
   */
  async cancelQuestion(id) {
    assert(id, 'id is required')

    try {
      await axios.post(
        `/api/questions/${id}/cancel`,
        {},
        {
          withCredentials: true,
        }
      )
    } catch (error) {
      console.error("Erreur lors de l'annulation de la question:", error)
      throw error
    }
  }

  /**
   * Récupère la liste des thèmes
   * @returns {Promise<Array>} Liste des thèmes
   */
  async getThemes() {
    try {
      const response = await axios.get('/api/questions/theme', {
        withCredentials: true,
      })
      return response.data.filter(Boolean).sort()
    } catch (error) {
      console.error('Erreur lors de la récupération des thèmes:', error)
      throw error
    }
  }

  /**
   * Récupère la liste des types
   * @returns {Promise<Array>} Liste des types
   */
  async getTypes() {
    try {
      const response = await axios.get('/api/questions/types', {
        withCredentials: true,
      })
      return response.data
    } catch (error) {
      console.error('Erreur lors de la récupération des types:', error)
      throw error
    }
  }

  /**
   * Importe des questions depuis un fichier Excel
   * @param {File} file - Fichier Excel à importer
   * @returns {Promise<Object>} Résultat de l'import
   */
  async importQuestions(file) {
    assert(file, 'file is required')

    try {
      const formData = new FormData()
      formData.append('excel', file)

      const response = await axios.post('/api/questions/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        withCredentials: true,
        timeout: 600000, // 10 minutes
      })
      return response.data
    } catch (error) {
      console.error("Erreur lors de l'import des questions:", error)
      throw error
    }
  }

  /**
   * Exporte les questions vers un fichier Excel
   * @returns {Promise<Blob>} Fichier Excel
   */
  async exportQuestions() {
    try {
      const response = await axios.post(
        '/api/questions/export',
        {},
        {
          responseType: 'blob',
          withCredentials: true,
        }
      )
      return response.data
    } catch (error) {
      console.error("Erreur lors de l'export des questions:", error)
      throw error
    }
  }

  /**
   * Effectue une action de masse sur plusieurs questions
   * @param {string} action - Action à effectuer ('cancel' ou 'delete')
   * @param {Array<string>} questionIds - IDs des questions concernées
   * @returns {Promise<Object>} Résultat de l'action de masse
   */
  async bulkActions(action, questionIds) {
    assert(action, 'action is required')
    assert(questionIds, 'questionIds is required')
    assert(Array.isArray(questionIds), 'questionIds must be an array')

    try {
      const response = await axios.post(
        '/api/questions/bulk-actions',
        {
          action,
          questionIds,
        },
        {
          withCredentials: true,
        }
      )
      return response.data
    } catch (error) {
      console.error('Erreur lors de l\'action de masse:', error)
      throw error
    }
  }

  /**
   * Récupère les IDs des questions selon le scope et les filtres
   * @param {string} scope - 'all' ou 'temporary'
   * @param {Object} filters - Filtres à appliquer (même format que getQuestions)
   * @returns {Promise<Object>} { ids: Array<string>, total: number, scope: string }
   */
  async getQuestionIds(scope, filters = {}) {
    assert(scope, 'scope is required')
    assert(['all', 'temporary'].includes(scope), 'scope must be "all" or "temporary"')

    const queryParams = {
      scope,
    }

    // Ajouter les filtres optionnels
    if (filters.search !== null && filters.search !== undefined) {
      queryParams.search = filters.search
    }
    if (filters.theme !== null && filters.theme !== undefined) {
      queryParams.theme = filters.theme
    }
    if (filters.type !== null && filters.type !== undefined) {
      queryParams.type = filters.type
    }
    if (filters.level !== null && filters.level !== undefined) {
      queryParams.level = filters.level
    }
    if (filters.audio !== null && filters.audio !== undefined) {
      queryParams.audio = filters.audio
    }
    if (filters.versions !== null && filters.versions !== undefined && filters.versions.length > 0) {
      queryParams.versions = filters.versions
    }
    if (filters.language !== null && filters.language !== undefined) {
      queryParams.language = filters.language
    }

    try {
      const response = await axios.get('/api/questions/ids', {
        params: queryParams,
        withCredentials: true,
      })
      return response.data
    } catch (error) {
      console.error('Erreur lors de la récupération des IDs:', error)
      throw error
    }
  }
}

export default new QuestionService()
